import wilayasData from "@/data/wilayas.json";
import communesData from "@/data/communes.json";
import { useMemo } from "react";

export interface Wilaya {
  state_id: string;
  state_name: string;
  state_name_ar?: string;
}

export interface Commune {
  city_id: string;
  city_name: string;
  state_id: string;
  city_name_ar?: string;
}

type Locale = "fr" | "ar";

const wilayas: Wilaya[] = wilayasData as Wilaya[];
const communes: Commune[] = communesData as Commune[];

/** Nom d'une wilaya dans la locale demandée (fallback FR si AR absent). */
export const getWilayaLabel = (w: Wilaya, locale: Locale = "fr") =>
  locale === "ar" && w.state_name_ar ? w.state_name_ar : w.state_name;

/** Nom d'une commune dans la locale demandée (fallback FR si AR absent). */
export const getCommuneLabel = (c: Commune, locale: Locale = "fr") =>
  locale === "ar" && c.city_name_ar ? c.city_name_ar : c.city_name;

/** Trouve une wilaya par son nom (FR ou AR). */
const findWilayaByName = (name: string) =>
  wilayas.find((w) => w.state_name === name || w.state_name_ar === name);

export const useWilayas = (locale: Locale = "fr") => {
  return useMemo(
    () => wilayas.map((w) => ({ ...w, label: getWilayaLabel(w, locale) })),
    [locale]
  );
};

export const useCommunesByWilaya = (wilayaName: string, locale: Locale = "fr") => {
  return useMemo(() => {
    if (!wilayaName) return [];
    const wilaya = findWilayaByName(wilayaName);
    if (!wilaya) return [];
    return communes
      .filter((c) => c.state_id === wilaya.state_id)
      .map((c) => ({ ...c, label: getCommuneLabel(c, locale) }))
      .sort((a, b) => a.label.localeCompare(b.label, locale === "ar" ? "ar" : "fr"));
  }, [wilayaName, locale]);
};

export const getAllWilayaNames = (locale: Locale = "fr") =>
  wilayas.map((w) => getWilayaLabel(w, locale)).sort((a, b) =>
    a.localeCompare(b, locale === "ar" ? "ar" : "fr")
  );

export const getCommunesForWilaya = (wilayaName: string, locale: Locale = "fr") => {
  const wilaya = findWilayaByName(wilayaName);
  if (!wilaya) return [];
  return communes
    .filter((c) => c.state_id === wilaya.state_id)
    .map((c) => getCommuneLabel(c, locale))
    .sort((a, b) => a.localeCompare(b, locale === "ar" ? "ar" : "fr"));
};

/** Retourne toutes les wilayas (ouverture nationale). Alias de getAllWilayaNames. */
export const getMVPWilayaNames = getAllWilayaNames;
